import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-transparent border border-blue-100/15 px-4 py-3 text-sm font-light text-white placeholder:text-blue-100/30 focus:border-[#4F7CFF] focus:outline-none transition-colors duration-300";
const labelCls = "font-meta text-[10px] tracking-[0.3em] text-blue-300/60 block mb-2";

const GROUPS = [
  {
    name: "HERO (OPENING SCREEN)",
    items: [
      { key: "hero_eyebrow", label: "SMALL TOP LINE" },
      { key: "hero_statement", label: "STATEMENT UNDER YOUR NAME" },
    ],
  },
  {
    name: "ABOUT",
    items: [
      { key: "about_headline", label: "HEADLINE" },
      { key: "about_p1", label: "PARAGRAPH 1", big: true },
      { key: "about_p2", label: "PARAGRAPH 2", big: true },
      { key: "about_p3", label: "PARAGRAPH 3", big: true },
      { key: "about_p4", label: "PARAGRAPH 4", big: true },
      { key: "about_closer", label: "CLOSING LINE", big: true },
    ],
  },
  {
    name: "CHAPTERS",
    items: [
      { key: "philosophy_p1", label: "PHILOSOPHY — PARAGRAPH 1", big: true },
      { key: "philosophy_p2", label: "PHILOSOPHY — PARAGRAPH 2", big: true },
      { key: "commentary_headline", label: "COMMENTARY — HEADLINE", big: true },
      { key: "commentary_text", label: "COMMENTARY — TEXT", big: true },
      { key: "commentary_callout", label: "COMMENTARY — QUOTED LINE", big: true },
      { key: "technology_headline", label: "TECHNOLOGY — HEADLINE", big: true },
      { key: "technology_text", label: "TECHNOLOGY — TEXT", big: true },
      { key: "nextgen_centerpiece", label: "NEXT GENERATION — BIG QUOTE", big: true },
      { key: "nextgen_text", label: "NEXT GENERATION — TEXT", big: true },
    ],
  },
  {
    name: "FINAL SCREEN",
    items: [
      { key: "final_statement", label: "FINAL BIG STATEMENT", big: true },
      { key: "final_meta", label: "NAME / AGE / YEAR LINE" },
      { key: "final_sub", label: "SMALL LINE UNDER IT" },
    ],
  },
];

const Field = ({ label, value, big, saved, onChange, onSave, testid }) => (
  <div data-testid={testid}>
    <label className={labelCls}>{label}</label>
    {big ? (
      <textarea rows={3} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} resize-y leading-relaxed`} data-testid={`${testid}-input`} />
    ) : (
      <input value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`${testid}-input`} />
    )}
    <button
      onClick={onSave}
      data-testid={`${testid}-save`}
      className={`mt-2 font-meta text-[10px] tracking-[0.25em] border px-4 py-2 transition-colors duration-200 ${
        saved ? "border-[#4F7CFF] bg-[#4F7CFF] text-white" : "border-blue-100/20 text-blue-100/60 hover:border-[#4F7CFF] hover:text-white"
      }`}
    >
      {saved ? "SAVED ✓" : "SAVE"}
    </button>
  </div>
);

export const SiteTextEditor = ({ adminKey, entries }) => {
  const [values, setValues] = useState(null);
  const [savedKey, setSavedKey] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/content`)
      .then((r) => setValues(r.data))
      .catch(() => setValues({}));
  }, []);

  const save = async (key, value) => {
    setErr(null);
    try {
      await axios.put(`${API}/content/${key}`, { value }, { headers: { "X-Admin-Key": adminKey } });
      setSavedKey(key);
      setTimeout(() => setSavedKey((s) => (s === key ? null : s)), 2500);
    } catch (e) {
      setErr(e.response?.status === 401 ? "Wrong access key — text not saved." : "Save failed. Try again.");
    }
  };

  if (values === null) return null;
  const statements = Array.isArray(values.statements) ? values.statements : [];

  const updateStatement = (i, patch) => {
    const st = statements.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
    setValues({ ...values, statements: st });
  };

  return (
    <div className="mt-16 border-t border-blue-100/10 pt-10" data-testid="site-text-editor">
      <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Site text</h2>
      <p className="text-sm font-light text-blue-100/60 mt-3 max-w-xl">
        Every word on the site, editable. Change it, tap SAVE, it's live. To move a piece to a
        different spot in the timeline, EDIT it above and change its date.
      </p>

      <div className="mt-8 mb-10">
        <span className={labelCls}>FEATURED PIECE ON THE HOMEPAGE</span>
        <select
          value={values.featured_slug || ""}
          onChange={(e) => {
            setValues({ ...values, featured_slug: e.target.value });
            save("featured_slug", e.target.value);
          }}
          data-testid="featured-select"
          className={`${inputCls} bg-[#05070F] cursor-pointer`}
        >
          {entries.map((en) => (
            <option key={en.slug} value={en.slug} className="bg-[#05070F]">
              {en.title}
            </option>
          ))}
        </select>
        {savedKey === "featured_slug" && (
          <p className="font-meta text-[10px] tracking-[0.25em] text-[#4F7CFF] mt-2">SAVED ✓</p>
        )}
      </div>

      <p className={labelCls}>FULL-SCREEN STATEMENTS (8 SPOTS — EMPTY TEXT HIDES THAT SPOT)</p>
      <div className="space-y-4 mb-10 mt-2">
        {statements.map((s, i) => (
          <div key={i} className="border border-blue-100/15 p-4" data-testid={`statement-edit-${i}`}>
            <span className="font-meta text-[9px] tracking-[0.3em] text-blue-300/50 block mb-2">
              STATEMENT {i + 1}
            </span>
            <input
              value={s.text}
              onChange={(e) => updateStatement(i, { text: e.target.value })}
              placeholder="STATEMENT TEXT"
              className={inputCls}
              data-testid={`statement-text-${i}`}
            />
            <div className="flex gap-3 mt-3">
              <input
                value={s.label || ""}
                onChange={(e) => updateStatement(i, { label: e.target.value })}
                placeholder="SMALL LABEL (OPTIONAL)"
                className={inputCls}
                data-testid={`statement-label-${i}`}
              />
              <button
                onClick={() => save("statements", statements)}
                data-testid={`statement-save-${i}`}
                className={`shrink-0 font-meta text-[10px] tracking-[0.25em] border px-4 transition-colors duration-200 ${
                  savedKey === "statements"
                    ? "border-[#4F7CFF] bg-[#4F7CFF] text-white"
                    : "border-blue-100/20 text-blue-100/60 hover:border-[#4F7CFF] hover:text-white"
                }`}
              >
                {savedKey === "statements" ? "SAVED ✓" : "SAVE"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {GROUPS.map((g) => (
        <div key={g.name} className="mb-10">
          <p className={labelCls}>{g.name}</p>
          <div className="space-y-5 mt-2">
            {g.items.map((it) => (
              <Field
                key={it.key}
                label={it.label}
                big={it.big}
                testid={`site-text-${it.key}`}
                value={values[it.key] ?? ""}
                saved={savedKey === it.key}
                onChange={(v) => setValues({ ...values, [it.key]: v })}
                onSave={() => save(it.key, values[it.key] ?? "")}
              />
            ))}
          </div>
        </div>
      ))}

      {err && (
        <p className="font-meta text-xs tracking-[0.2em] text-red-400 border border-red-400/30 px-4 py-3" data-testid="site-text-error">
          {err}
        </p>
      )}
    </div>
  );
};
